import {
  ActivateSelfFundedOfferResultTypenames,
  ActivateSelfFundedOfferSuccess,
  OfferChoicesResultTypenames,
  OfferChoicesSuccess,
} from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import SelectableCardSquare from 'features/rewards/components/SelectableCardSquare/SelectableCardSquare';
import useActivateSelfFundedOffer from 'features/rewards/hooks/selfFundedOffers/useActivateSelfFundedOffer';
import useGetOfferChoices from 'features/rewards/hooks/selfFundedOffers/useGetOfferChoices';
import useRefreshSelfFundedOfferTile from 'features/rewards/hooks/selfFundedOffers/useRefreshSelfFundedOfferTile';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { parseOptionalGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleXLText from 'shared/components/vxl/typography/primary/title/TitleXLText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusLogScreenAnalytics } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

import { useSelfFundedOffer } from './ContextProvider/SelfFundedOfferContextProvider';

interface ExtendedOfferChoicesSuccess extends OfferChoicesSuccess {
  offersPageTitle: string;
}

interface ExtendedActivateSelfFundedOfferSuccess extends ActivateSelfFundedOfferSuccess {
  successTitle: string;
}

const isExtendedOfferChoicesSuccess = (
  page: OfferChoicesSuccess | null,
): page is ExtendedOfferChoicesSuccess => {
  return (
    page?.__typename === OfferChoicesResultTypenames.OfferChoicesSuccess &&
    'offersPageTitle' in page
  );
};

const isExtendedActivateSelfFundedOfferSuccess = (
  page: ActivateSelfFundedOfferSuccess | null,
): page is ExtendedActivateSelfFundedOfferSuccess => {
  return (
    page?.__typename ===
      ActivateSelfFundedOfferResultTypenames.ActivateSelfFundedOfferSuccess &&
    'successTitle' in page
  );
};

const OfferSelectionScreen = () => {
  const router = useRouter();
  const titleTextLineLimit = 2;
  const subTitleTextLineLimit = 3;
  const insets = useSafeAreaInsets();
  const {
    offersData,
    loading,
    errorData,
    retryFetchOffers,
    retryAttempt: fetchRetryAttempt,
    maxRetryAttempts: fetchMaxRetryAttempts,
  } = useGetOfferChoices();
  const {
    activatedOfferData,
    mutationFetching,
    activateOfferError,
    activateOffer,
    retryActivateOffer,
    retryAttempt: activateRetryAttempt,
    maxRetryAttempts: activateMaxRetryAttempts,
  } = useActivateSelfFundedOffer();
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const { t: translatedText } = useTranslation('rewards', {
    keyPrefix: 'selfFundedOffers',
  });
  const { refreshSelfFundedOfferTile } = useRefreshSelfFundedOfferTile();
  const { setDisplayAnalytics, setCtaAnalytics } = useSelfFundedOffer();
  const { refreshTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_BANKING,
  ]);

  const analytics = parseOptionalGQLAnalyticsEvent(
    isExtendedOfferChoicesSuccess(offersData)
      ? offersData.displayAnalytics
      : undefined,
  );

  useViewDidFocusLogScreenAnalytics(analytics);

  const logOfferActivationErrorEvent = usePressableAnalytics({
    analyticsId: 'offerActivationError',
    customAttributes: {
      error_count: activateRetryAttempt,
    },
  });

  const handleCardSelection = (cardId: string) => {
    setSelectedCardId((prevId) => (prevId === cardId ? null : cardId));
  };

  const handleDoneAction = () => {
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace('/(authenticated)/(tabs)');
    }
  };

  const handleRetryAction = () => {
    if (errorData) {
      // Retry fetching offers
      retryFetchOffers();
    } else if (activateOfferError && selectedCardId) {
      // Retry activating the selected offer
      retryActivateOffer(selectedCardId);
    }
  };

  const handleActivateAction = () => {
    if (!selectedCardId) {
      return;
    }

    activateOffer(selectedCardId);
  };

  useEffect(() => {
    if (
      isExtendedActivateSelfFundedOfferSuccess(activatedOfferData) &&
      activatedOfferData.isSuccess
    ) {
      refreshSelfFundedOfferTile({ requestPolicy: 'network-only' });
      refreshTiles();

      const { displayAnalytics, offerDetailsCtaAnalytics } = activatedOfferData;
      if (displayAnalytics && offerDetailsCtaAnalytics) {
        setDisplayAnalytics(displayAnalytics);
        setCtaAnalytics(offerDetailsCtaAnalytics);
      }

      router.push({
        pathname: '/rewards/self-funded-offers/activation-confirmation',
        params: {
          offerId: activatedOfferData.activatedOfferId,
          title: activatedOfferData.successTitle,
          subtitle: activatedOfferData.subTitle,
        },
      });
    }
  }, [
    activatedOfferData,
    refreshSelfFundedOfferTile,
    refreshTiles,
    setCtaAnalytics,
    setDisplayAnalytics,
    router,
  ]);

  useEffect(() => {
    if (activateOfferError) {
      logOfferActivationErrorEvent();
    }
  }, [activateOfferError, logOfferActivationErrorEvent]);

  const gridData = useMemo(() => {
    const items = offersData?.bodyItems || [];
    if (items.length % 2 !== 0) {
      return [...items, { id: 'spacer-item', title: '', iconUrl: '' }];
    }
    return items;
  }, [offersData?.bodyItems]);

  // Determine if we should show the primary retry button (avoid nested ternaries for ESLint rule no-nested-ternary)
  const canRetryFetchOffers =
    !!errorData && fetchRetryAttempt < fetchMaxRetryAttempts;
  const canRetryActivateOffer =
    !!activateOfferError && activateRetryAttempt < activateMaxRetryAttempts;
  const primaryErrorButton =
    canRetryFetchOffers || canRetryActivateOffer
      ? {
          title: translatedText('error.retryButtonTitle'),
          onPress: handleRetryAction,
        }
      : undefined;

  return (
    <View style={[styles.container, { paddingBottom: insets.bottom }]}>
      <Stack.Screen
        options={{
          title: '',
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />

      <View style={styles.topContainer}>
        <View style={styles.textContainer}>
          {isExtendedOfferChoicesSuccess(offersData) ? (
            <TitleXLText numberOfLines={titleTextLineLimit}>
              {offersData.offersPageTitle}
            </TitleXLText>
          ) : null}
          <BodyBaseText numberOfLines={subTitleTextLineLimit}>
            {offersData?.subTitle}
          </BodyBaseText>
        </View>

        <View style={styles.offersContainer}>
          <FlatList
            data={gridData}
            renderItem={({ item }) => {
              if (item.id === 'spacer-item') {
                return <View style={styles.gridItem} />;
              }
              return (
                <View style={styles.gridItem}>
                  <SelectableCardSquare
                    titleText={item.title}
                    iconUrl={{ uri: item.iconUrl }}
                    isSelected={selectedCardId === item.id}
                    onCardPress={() => handleCardSelection(item.id)}
                  />
                </View>
              );
            }}
            keyExtractor={(item) => item.id}
            numColumns={2}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.gridContentContainer}
            columnWrapperStyle={styles.gridRow}
          />
        </View>
      </View>

      <View style={styles.footerContainer}>
        <HairLineDivider />

        <View style={styles.button}>
          <Button
            type="PRIMARY"
            label={
              offersData?.buttonTitle ?? translatedText('activateButtonTitle')
            }
            isDisabled={!selectedCardId}
            isLoading={mutationFetching}
            onPress={handleActivateAction}
          />
        </View>
      </View>

      <ScreenTransitionOverlay
        isLoading={loading || false}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          errorData || activateOfferError ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              screenAnalyticsEventKey="cashbackRewardsErrorEncountered"
              messageHeadline={
                errorData ? errorData.name : activateOfferError?.name
              }
              messageBody={
                errorData ? errorData.message : activateOfferError?.message
              }
              primaryButton={primaryErrorButton}
              secondaryButton={{
                title: translatedText('error.buttonTitle'),
                onPress: handleDoneAction,
              }}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  button: {
    paddingHorizontal: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    justifyContent: 'space-between',
    paddingVertical: theme.spacing.m,
  },
  footerContainer: {
    gap: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  gridContentContainer: {
    gap: theme.spacing.s,
  },
  gridItem: {
    flex: 1,
  },
  gridRow: {
    gap: theme.spacing.s,
  },
  offersContainer: {
    flexShrink: 1,
    marginTop: theme.spacing.l,
  },
  textContainer: {
    gap: theme.spacing.s,
  },
  topContainer: {
    flexShrink: 1,
    paddingHorizontal: theme.spacing.m,
  },
});

export default OfferSelectionScreen;
